import {
	dateToUTCString,
	startOf,
	endOf,
	addUnit,
	isBefore,
	duration,
	fromNow,
	utcTimestampToLocalDate,
	formatDate,
} from "@sembark-travel/datetime-utils"
import {
	Box,
	Button,
	Icons,
	Inline,
	Heading,
	Stack,
	Text,
	Col,
	Divider,
	Grid,
	Component,
	useTimeout,
	Alert,
	Table,
	TimeDuration,
	Time,
	joinAttributes,
	Money,
	RelativeTime,
	Badge,
	Select,
	Input,
	RadioInput,
} from "@sembark-travel/ui-base"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import * as Validator from "yup"
import {
	ITripSource,
	SelectTripSourceContact,
	SelectTripSources,
} from "../TripSources"
import { ITrip } from "../Trips"
import {
	Form,
	Field,
	TextInputField,
	TextAreaInputField,
	SelectField,
	validateFormValues,
	withServerErrors,
	DatePickerField,
	SubmissionError,
	PhoneInputField,
	GetFieldValue,
	isTruthy,
	EmptyNumberValidator,
	PhoneNumberValidator,
	useFieldValue,
	RadioInputField,
	ErrorMessage,
	useForm,
	getEditablePhoneNumber,
} from "@sembark-travel/ui-form"
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { PhoneNumber, type IContact, Email } from "../Contacts"
import { type TChildrenArray, ChildrenInputField } from "../Tourists"
import { SelectUsers } from "../Users"
import { IUser, PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import {
	TAddressInputFieldValue,
	transformAddressToEditableValue,
	transformAddressToStorableValue,
} from "../Addresses"
import { AddressInputField } from "../Addresses"
import { ITripSourceTag } from "../TripSources/store"
import { ITag, SelectTags } from "../Tags"
import { SelectWorldCountries } from "../World"
import { ILocation } from "../Locations"
import { ILeadPurpose, LeadXHR } from "./store"
import { IHotel, SelectHotels } from "../Hotels"
import { Link } from "@sembark-travel/ui-router"
import useSWR from "swr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useNavigate } from "../router-utils"
import { TLeadChannel } from "../LeadChannels"
import { SelectLeadChannels } from "../LeadChannels"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { SelectLeadPurposes } from "./List"
import { useDebounce } from "../shared/mergeUtils"

const validationSchema = Validator.object().shape({
	trip_id: Validator.string(),
	start_date: Validator.date().when(
		["lead_purpose", "link_to_existing_trip"],
		([leadPurpose, isLinked], schema) => {
			if (isLinked === "1") {
				return schema.optional()
			}
			if (leadPurpose?.config.travel_dates) {
				return schema.required("Start date is required")
			}
			return schema.optional()
		}
	),
	no_of_days: EmptyNumberValidator().when(
		"link_to_existing_trip",
		(values, schema) => {
			const isLinked = values[0]
			if (isLinked === "1") {
				return schema.optional()
			}
			return schema
				.positive(
					"Number of days should be a positive integer e.g. 1, 2, 3 etc"
				)
				.integer(
					"Number of days should be a positive integer e.g. 1, 2, 3 etc."
				)
				.required("Number of days is required")
		}
	),
	no_of_nights: EmptyNumberValidator().when(
		["lead_purpose", "link_to_existing_trip"],
		([leadPurpose, isLinked], schema) => {
			if (isLinked === "1") {
				return schema.optional()
			}
			if (isRequired(leadPurpose?.config?.travel_dates)) {
				return schema
					.integer("Number of nights should be an integer e.g. 0, 1, 2 etc.")
					.min(0, "Number of nights should not be negative")
					.required("Number of nights is required")
			}
			return schema.optional()
		}
	),

	no_of_adults: EmptyNumberValidator().when(
		["lead_purpose", "link_to_existing_trip"],
		([leadPurpose, isLinked], schema) => {
			if (isLinked === "1") {
				return schema.optional()
			}
			if (isRequired(leadPurpose?.config?.travel_dates)) {
				return schema
					.positive("Number of adults should be a positive integer")
					.integer("Number of adults should be a positive integer")
					.required("Number of adults field is required")
			}
			return schema.optional()
		}
	),
	children: Validator.array().of(
		Validator.object().shape({
			count: EmptyNumberValidator()
				.positive("Number of children should be positive integer")
				.integer("Number of children should be positive integer"),
			age: EmptyNumberValidator().positive(
				"Child age should a positive number"
			),
		})
	),
	trip_source: Validator.object().when("is_agent", ([isAgent], schema) =>
		isTruthy(isAgent)
			? schema.required("Query Source is required")
			: schema.nullable()
	),
	trip_source_contact: Validator.object().when(
		"is_agent",
		([isAgent], schema) =>
			isTruthy(isAgent)
				? schema.required("Contact / Enquiry Person is required")
				: schema.nullable()
	),
	tourist: Validator.object().when(
		["is_agent", "trip_source", "trip_source_contact"],
		([isAgent, tripSource, tripSourceContact], schema) => {
			const nameValidator = Validator.string().max(
				191,
				"Please use 191 or fewer character for name"
			)
			const emailValidator = Validator.string().email(
				"Please provide a valid email address"
			)
			const phoneValidator = PhoneNumberValidator("Phone Number", true).when(
				"name",
				([touristName]) => {
					if (touristName) {
						return PhoneNumberValidator("Phone Number", false)
					}
					return PhoneNumberValidator("Phone Number", true)
				}
			)
			const hasValue = (value: unknown) =>
				typeof value === "string" ? Boolean(value.trim()) : Boolean(value)

			const hasPhoneNumber = (phoneNumbers: unknown) =>
				typeof phoneNumbers === "object" &&
				phoneNumbers !== null &&
				hasValue((phoneNumbers as { number?: unknown }).number)

			const hasAddress = (address?: TAddressInputFieldValue | null) =>
				Boolean(address?.location)

			return schema.shape({
				name:
					isTruthy(isAgent) && tripSourceContact
						? nameValidator
								.nullable()
								.test(
									"name-required",
									"Please provide the guest details",
									function (value) {
										const { phone_numbers, address, email, nationality } =
											this.parent || {}

										if (
											hasPhoneNumber(phone_numbers) ||
											hasAddress(address) ||
											hasValue(email) ||
											hasValue(nationality)
										) {
											return hasValue(value)
										}

										return true
									}
								)
						: nameValidator.required("Please provide the guest details"),
				email: emailValidator,
				phone_numbers: phoneValidator,
			})
		}
	),
	comments: Validator.string().nullable(),
	tags: Validator.array()
		.of(
			Validator.object().shape({
				name: Validator.string().required("Tag name is required"),
			})
		)
		.optional(),
	is_agent: Validator.string()
		.oneOf(["1", "0"])
		.when("link_to_existing_trip", (values, schema) => {
			const isLinked = values[0]
			if (isLinked === "1") {
				return schema.optional()
			}
			return schema.required("Please select B2B or B2C")
		}),

	lead_channel: Validator.object().required("Lead channel is required"),
	lead_purpose: Validator.object().required("Lead purpose is required"),

	hotels_of_interest: Validator.array()
		.of(Validator.object())
		.when("lead_purpose", (values, schema) => {
			const leadPurpose = values[0] as ILeadPurpose | null
			if (isRequired(leadPurpose?.config?.hotels_of_interest)) {
				return schema
					.min(1, "Please select at least one hotel of interest")
					.required("Hotel of Interest is required")
			}
			return schema.optional()
		}),
	sales_team: Validator.object().required("Sales Team is required"),
})

const validate = validateFormValues(validationSchema)

interface NewItemSchema {
	link_to_existing_trip: "1" | "0"
	trip_id?: string
	reference_id?: string
	start_date?: Date
	no_of_days: number
	no_of_nights: number
	no_of_adults: number
	trip_source?: ITripSource
	children: TChildrenArray
	comments?: string
	trip_source_contact?: IContact
	sales_team?: IUser
	tourist?: {
		name?: string
		email?: string
		phone_numbers: {
			number: string
			country_code?: string
			is_primary?: boolean
		}
		salutation?: "Mr." | "Mrs." | "Ms."
		nationality?: ILocation["country"]
		address?: TAddressInputFieldValue
	}
	tags: Array<ITag>
	lead_channel: TLeadChannel | null
	lead_purpose: ILeadPurpose | null
	hotels_of_interest: Array<IHotel> | null
	is_agent?: "1" | "0"
	trip?: ITrip
	suggested_trips?: ITrip[]
}

const defaultInitialValues: NewItemSchema = {
	link_to_existing_trip: "0",
	reference_id: "",
	start_date: undefined,
	no_of_days: 2,
	no_of_nights: 1,
	no_of_adults: 1,
	children: [],
	trip_source: undefined,
	comments: "",
	tourist: {
		name: "",
		email: "",
		phone_numbers: {
			number: "",
			country_code: "IN",
			is_primary: true,
		},
		address: undefined,
		salutation: "Mr.",
		nationality: undefined,
	},
	trip_source_contact: undefined,
	sales_team: undefined,
	tags: [],
	lead_channel: null,
	lead_purpose: null,
	hotels_of_interest: [],
	is_agent: undefined,
}

type TLeadSubmitIntent = "SAVE_LEAD" | "UPDATE_LEAD" | "MOVE_TO_TRIP"

export function LeadDetailsForm({
	onSubmit,
	onCancel,
	initialValues: initialValuesFromProps,
	readOnlySource,
	readOnlyDates,
	readOnlyPax,
	hideComments,
	hideTags,
	showTeam,
	showDuplicateExceptId,
	disableCreateNew,
	mode = "create",
}: {
	onSubmit: (
		data: Record<string, unknown>,
		intent: TLeadSubmitIntent
	) => Promise<void>
	onCancel?: () => void
	initialValues?: Partial<NewItemSchema>
	readOnlySource?: boolean
	readOnlyDates?: boolean
	readOnlyPax?: boolean
	hideComments?: boolean
	hideTags?: boolean
	showTeam?: boolean
	showDuplicateExceptId?: number
	disableCreateNew?: boolean
	mode?: "create" | "edit"
}) {
	const { user } = useAuthUser()
	const [hideFooter, setHideFooter] = useState(false)
	const [submissionIntent, setSubmissionIntent] =
		useState<TLeadSubmitIntent>("SAVE_LEAD")

	const [initialValues] = useState<NewItemSchema>(() => {
		const derivedFromTrip = initialValuesFromProps?.trip
			? deriveFormValuesFromTrip(initialValuesFromProps.trip)
			: {}
		const payload: NewItemSchema = {
			...defaultInitialValues,
			...initialValuesFromProps,
			sales_team:
				initialValuesFromProps?.sales_team || (user ? user : undefined),
			tourist: initialValuesFromProps?.tourist || defaultInitialValues.tourist,
			no_of_nights: initialValuesFromProps?.no_of_days
				? initialValuesFromProps?.no_of_days - 1
				: defaultInitialValues.no_of_nights,
			...derivedFromTrip,
		}
		return payload
	})

	return (
		<Form<NewItemSchema>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(async (values) => {
				const {
					start_date,
					no_of_adults,
					no_of_days,
					children = [],
					trip_source,
					reference_id,
					tourist,
					trip_source_contact,
					comments,
					sales_team,
					lead_channel,
					hotels_of_interest,
					lead_purpose,
					is_agent,
					tags,
				} = values
				const config = lead_purpose?.config
				const sendTravelDates = config?.travel_dates
				const sendHotelsOfInterest = config?.hotels_of_interest
				let data = {}

				if (values.link_to_existing_trip === "1") {
					data = {
						link_to_existing_trip: 1,
						is_agent: isTruthy(is_agent) ? 1 : 0,
						trip_id: Number(values.trip_id),
						lead_channel_id: lead_channel?.id,
						lead_purpose_id: lead_purpose?.id,
						hotels_of_interest: sendHotelsOfInterest
							? hotels_of_interest?.map((h) => h.id)
							: undefined,
						sales_team: [sales_team?.id],
						trip_source: isTruthy(is_agent) ? trip_source?.id : undefined,
						trip_source_contact: isTruthy(is_agent)
							? trip_source_contact?.id
							: undefined,
						tourist: tourist?.name
							? {
									name: tourist.name,
									email: tourist.email || undefined,
									phone_numbers: [
										{
											...tourist.phone_numbers,
											is_primary: isTruthy(tourist.phone_numbers.is_primary)
												? 1
												: 0,
										},
									],
									address: tourist.address
										? transformAddressToStorableValue(tourist.address)
										: null,
									salutation: tourist.salutation,
									nationality_id: tourist.nationality?.id ?? undefined,
								}
							: null,
						reference_id: reference_id,
					}
				} else {
					data = {
						start_date:
							sendTravelDates && start_date
								? dateToUTCString(startOf(start_date, "day"))
								: undefined,
						start_date_local:
							sendTravelDates && start_date
								? formatDate(startOf(start_date, "day"), "YYYY-MM-DD")
								: undefined,
						end_date:
							sendTravelDates && start_date
								? dateToUTCString(
										endOf(addUnit(start_date, no_of_days - 1, "day"), "day")
									)
								: undefined,
						end_date_local:
							sendTravelDates && start_date
								? formatDate(
										endOf(addUnit(start_date, no_of_days - 1, "day"), "day"),
										"YYYY-MM-DD"
									)
								: undefined,
						no_of_days: sendTravelDates ? no_of_days || undefined : undefined,
						no_of_adults: sendTravelDates ? no_of_adults : undefined,
						children: sendTravelDates ? children : [],
						reference_id: reference_id,
						trip_source: isTruthy(is_agent) ? trip_source?.id : undefined,
						trip_source_contact: isTruthy(is_agent)
							? trip_source_contact?.id
							: undefined,
						comments,
						sales_team: [sales_team?.id],
						tourist: tourist?.name
							? {
									name: tourist.name,
									email: tourist.email || undefined,
									phone_numbers: [
										{
											...tourist.phone_numbers,
											is_primary: isTruthy(tourist.phone_numbers.is_primary)
												? 1
												: 0,
										},
									],
									address: tourist.address
										? transformAddressToStorableValue(tourist.address)
										: null,
									salutation: tourist.salutation,
									nationality_id: tourist.nationality?.id ?? undefined,
								}
							: null,
						is_agent: isTruthy(is_agent) ? 1 : 0,
						tags: tags.map((tag) => tag.name),
						lead_channel_id: lead_channel?.id,
						lead_purpose_id: lead_purpose?.id,
						hotels_of_interest: sendHotelsOfInterest
							? hotels_of_interest?.map((h) => h.id)
							: undefined,
					}
				}
				await onSubmit(data, submissionIntent)
			})}
			subscription={{
				submitting: true,
				hasValidationErrors: true,
				submitFailed: true,
			}}
		>
			{({
				submitting,
				handleSubmit,
				form,
				submitFailed,
				hasValidationErrors,
			}) => (
				<form noValidate onSubmit={handleSubmit}>
					<GetFieldValue name="link_to_existing_trip">
						{({ value: isLinked }) => (
							<>
								<Grid gap="4" marginBottom="6">
									<Col>
										<Stack
											height="full"
											padding="4"
											bgColor={
												disableCreateNew
													? "gray2"
													: !isTruthy(isLinked)
														? "accent"
														: "default"
											}
											as="label"
											cursor="pointer"
											borderWidth="1"
											borderRadius="md"
											borderColor={!isTruthy(isLinked) ? "primary" : "emphasis"}
											color={disableCreateNew ? "muted" : "black"}
											pointerEvents={disableCreateNew ? "none" : undefined}
										>
											<RadioInputField
												name="link_to_existing_trip"
												label="Create New Lead"
												help={<Text>New caller,query or exisiting</Text>}
												value="0"
												onChange={() => {
													form.change("link_to_existing_trip", "0")
													setHideFooter(false)
												}}
												disabled={disableCreateNew}
											/>
										</Stack>
									</Col>
									<Col>
										<Stack
											height="full"
											padding="4"
											bgColor={isTruthy(isLinked) ? "accent" : "default"}
											as="label"
											cursor="pointer"
											borderWidth="1"
											borderRadius="md"
											borderColor={isTruthy(isLinked) ? "primary" : "emphasis"}
										>
											<RadioInputField
												name="link_to_existing_trip"
												label="Link to Exisiting Trip"
												help={
													<Text>
														Search by Trip ID, guest, phone, source or
														destination
													</Text>
												}
												value="1"
												onChange={() =>
													form.change("link_to_existing_trip", "1")
												}
											/>
										</Stack>
									</Col>
								</Grid>
								{!isTruthy(isLinked) ? (
									<NewLead
										initialValues={initialValues}
										readOnlySource={readOnlySource}
										showTeam={showTeam}
										readOnlyDates={readOnlyDates}
										hideComments={hideComments}
										hideTags={hideTags}
										readOnlyPax={readOnlyPax}
										showDuplicateExceptId={showDuplicateExceptId}
									/>
								) : (
									<LinkToExisting
										setHideFooter={setHideFooter}
										initialValues={initialValues}
										showTeam={showTeam}
										readOnlySource={readOnlySource}
										disableCreateNew={disableCreateNew}
									/>
								)}

								{!hideFooter ? (
									<>
										<Divider />
										<Grid gap="4">
											<Col sm={12} md={{ offset: 4, span: 8 }}>
												<GetFieldValue<"0" | "1"> name="link_to_existing_trip">
													{({ value: isLinked }) => (
														<GetFieldValue<ILeadPurpose | null> name="lead_purpose">
															{({ value: leadPurpose }) => {
																const config = leadPurpose?.config
																const showMoveToTrip =
																	!isTruthy(isLinked) &&
																	(!leadPurpose ||
																		config?.move_to_trips !== false)
																return (
																	<Stack gap="4" paddingLeft={{ md: "4" }}>
																		<SubmissionError />
																		{submitFailed && hasValidationErrors && (
																			<Alert
																				status="error"
																				title="Please fill in all the required fields."
																			/>
																		)}
																		<Inline gap="4">
																			{isTruthy(isLinked) ? (
																				<Button
																					type="submit"
																					disabled={submitting}
																					onClick={() =>
																						setSubmissionIntent(
																							mode === "edit"
																								? "UPDATE_LEAD"
																								: "SAVE_LEAD"
																						)
																					}
																				>
																					{submitting
																						? "Linking..."
																						: "Link To Trip"}
																				</Button>
																			) : (
																				<>
																					{mode === "edit" ? (
																						<Button
																							level="secondary"
																							type="submit"
																							disabled={submitting}
																							onClick={() =>
																								setSubmissionIntent(
																									"UPDATE_LEAD"
																								)
																							}
																						>
																							{submitting &&
																							submissionIntent === "UPDATE_LEAD"
																								? "Updating..."
																								: "Update Details"}
																						</Button>
																					) : (
																						<Button
																							type="submit"
																							disabled={submitting}
																							onClick={() =>
																								setSubmissionIntent("SAVE_LEAD")
																							}
																						>
																							{submitting &&
																							submissionIntent === "SAVE_LEAD"
																								? "Saving..."
																								: "Save"}
																						</Button>
																					)}
																					{showMoveToTrip ? (
																						<Button
																							type="submit"
																							level={
																								mode === "edit"
																									? "primary"
																									: "secondary"
																							}
																							disabled={submitting}
																							onClick={() =>
																								setSubmissionIntent(
																									"MOVE_TO_TRIP"
																								)
																							}
																						>
																							{submitting &&
																							submissionIntent ===
																								"MOVE_TO_TRIP"
																								? "Moving..."
																								: "Move to Trips"}
																						</Button>
																					) : null}
																				</>
																			)}
																			{onCancel ? (
																				<Button
																					onClick={onCancel}
																					level="tertiary"
																				>
																					{mode === "edit" ? "Close" : "Cancel"}
																				</Button>
																			) : null}
																		</Inline>
																	</Stack>
																)
															}}
														</GetFieldValue>
													)}
												</GetFieldValue>
											</Col>
										</Grid>
									</>
								) : null}
							</>
						)}
					</GetFieldValue>
				</form>
			)}
		</Form>
	)
}

function ExistingTrips({
	children,
	exceptId,
}: {
	children: (props: {
		trips: IListResponse<ITrip> | undefined
	}) => React.ReactNode
	exceptId?: number
}) {
	const { value } = useFieldValue<null | {
		number: string
		country_code?: string
		is_primary: boolean
	}>("tourist.phone_numbers")
	const validPhoneNumbers = useMemo(() => {
		return value?.number ? [value.number] : []
	}, [value])

	const { set, clear } = useTimeout()
	const xhr = useXHR()
	const [existing, setExisting] = useState<IListResponse<ITrip> | undefined>(
		undefined
	)
	useEffect(() => {
		setExisting(undefined)
		if (!validPhoneNumbers.length) {
			return () => undefined
		}
		const abortController = new AbortController()
		set(() => {
			xhr
				.get<IListResponse<ITrip>>("/existing-trips", {
					params: {
						phone_numbers: validPhoneNumbers,
						with: ["given_price"],
						except_ids: exceptId ? [exceptId] : undefined,
					},
					signal: abortController.signal,
				})
				.then((resp) => {
					if (resp.data.data.length) {
						setExisting(resp.data)
					} else {
						setExisting(undefined)
					}
				})
				.catch(() => {
					return undefined
				})
		}, 500)
		return () => {
			clear()
			abortController.abort()
		}
	}, [validPhoneNumbers, set, clear, xhr, exceptId])
	return (
		<>{children({ trips: validPhoneNumbers?.length ? existing : undefined })}</>
	)
}

function QueriesTableView({ items }: { items: Array<ITrip> }) {
	return (
		<Table
			bordered
			responsive
			hover
			headers={["ID", "Guest", "B2B Source", "Destination", "Quote", "Team"]}
			rows={items.map((trip) => {
				const {
					destinations,
					nights: no_of_nights,
					days,
					latest_given_quote,
					given_quotes_count,
					trip_source,
					trip_source_contact,
					team,
					reference_id,
					tourist,
					no_of_adults,
					no_of_children,
					created_at,
				} = trip
				return [
					<Stack gap="1">
						<Text fontWeight="semibold">{trip.id}</Text>
						<Box fontSize="sm">
							{latest_given_quote ? (
								<Box color="primary">In Progress</Box>
							) : (
								<Box color="muted">New Query</Box>
							)}
						</Box>
					</Stack>,
					tourist ? (
						<Stack gap="px">
							<Text fontWeight="semibold">{tourist.name}</Text>
							<Box fontSize="sm" color="muted">
								{joinAttributes(
									<Text as="span">
										{no_of_adults}A
										{no_of_children ? `, ${no_of_children}C` : null}
									</Text>,
									[
										tourist.phone_numbers?.length,
										<PhoneNumber value={tourist.phone_numbers} iconOnly />,
									],
									[tourist.email, <Email value={tourist.email} iconOnly />]
								)}
							</Box>
						</Stack>
					) : null,
					<Stack gap="px">
						<Text fontWeight="semibold">{trip_source.short_name}</Text>
						{reference_id ? (
							<Text fontSize="sm">Ref# {reference_id}</Text>
						) : null}
						{trip_source_contact ? (
							<Inline alignItems="center" flexWrap="wrap" gap="2" fontSize="sm">
								<Box color="muted">{trip_source_contact.name}</Box>
								<Box display="inlineBlock">
									{joinAttributes([
										trip_source_contact.phone_numbers?.length,
										<PhoneNumber
											value={trip_source_contact.phone_numbers}
											iconOnly
										/>,
									])}
								</Box>
							</Inline>
						) : null}
					</Stack>,
					<Stack gap="px">
						<Text style={{ maxWidth: "140px" }} textOverflow="truncate">
							{destinations.map((d) => d.name).join(", ")}
						</Text>
						<Box fontSize="sm" color="muted">
							{joinAttributes(
								<Time timestamp={trip.start_date} />,
								<TimeDuration value={duration(days, "days")}>
									{no_of_nights}N
								</TimeDuration>
							)}
						</Box>
					</Stack>,
					latest_given_quote ? (
						<Box
							title={`Latest quote ${fromNow(
								utcTimestampToLocalDate(latest_given_quote.created_at)
							)}`}
						>
							<Text>{given_quotes_count} Q</Text>
							<Money
								amount={latest_given_quote.given_price}
								currency={latest_given_quote.given_currency}
								color="muted"
								fontSize="sm"
							/>
						</Box>
					) : (
						"-"
					),
					<Stack gap="px">
						{team?.length ? (
							<Stack style={{ width: "80px" }} gap="px">
								{team.map((t) => (
									<Text
										textOverflow="truncate"
										key={t.id}
										fontSize="sm"
										color="muted"
									>
										{t.name}
									</Text>
								))}
							</Stack>
						) : null}
						<RelativeTime fontSize="sm" timestamp={created_at} />
					</Stack>,
				]
			})}
		/>
	)
}

export function QuerySourceEditor({ showTeam }: { showTeam?: boolean }) {
	const { user } = useAuthUser()
	const { hasPermission } = useCheckPermissions()
	const isAdmin = hasPermission(PERMISSIONS.MANAGE_USERS)
	const { value: leadPurpose } = useFieldValue<ILeadPurpose | null>(
		"lead_purpose"
	)
	const { value: isLinked } = useFieldValue<"0" | "1">("link_to_existing_trip")
	const config = leadPurpose?.config
	return (
		<Grid gap="4">
			<Col xs={12} md={4}>
				<Stack gap="2">
					<Inline gap="2" alignItems="center">
						<Icons.ClipboardCopy size="6" />{" "}
						<Heading fontSize="lg">Lead Details</Heading>
					</Inline>
					<Text color="muted">Add the key details of this lead</Text>
				</Stack>
			</Col>
			<Col>
				<Grid gap="4">
					<Col xs={12} sm={6}>
						<GetFieldValue<TLeadChannel | null> name="lead_channel">
							{({ value }) => {
								return (
									<SelectField
										name="lead_channel"
										label="Lead Channel"
										select={SelectLeadChannels}
										hideAutoChannels
										fetchOnMount
										disabled={value?.is_auto === 1}
										searchable
									/>
								)
							}}
						</GetFieldValue>
					</Col>
					<Col xs={12} sm={5}>
						<SelectField
							key={`lead-purpose-${String(isLinked)}`}
							name="lead_purpose"
							label="Lead Purpose"
							select={SelectLeadPurposes}
							fetchOnMount
							linkToExisting={isTruthy(isLinked)}
							searchable
						/>
					</Col>

					{config?.hotels_of_interest !== false ? (
						<Col xs={12} sm={6}>
							<SelectField
								name="hotels_of_interest"
								label="Hotels of Interest"
								select={SelectHotels}
								creatable={isAdmin}
								secondaryLabel={
									!config?.hotels_of_interest ||
									config.hotels_of_interest === "conditional"
										? "optional"
										: ""
								}
								fetchOnMount
								multiple
							/>
						</Col>
					) : null}

					{showTeam ? (
						<Col xs={12} sm={5}>
							<GetFieldValue<IUser | null> name="sales_team">
								{({ value: salesTeam }) => (
									<Component
										initialState={salesTeam?.id === user?.id ? false : true}
									>
										{({ state, setState }) => (
											<Box>
												{!state ? (
													<Box>
														<Stack gap="1">
															<Text fontWeight="semibold">Sales Team</Text>
															<Box>
																<Button
																	onClick={() => setState(true)}
																	level="secondary"
																	title="Click to Edit"
																>
																	You <Icons.Pencil opacity="50" />
																</Button>
															</Box>
														</Stack>
													</Box>
												) : (
													<SelectField
														name="sales_team"
														select={SelectUsers}
														label="Sales Team"
														autoFocus
														fetchOnMount
													/>
												)}
											</Box>
										)}
									</Component>
								)}
							</GetFieldValue>
						</Col>
					) : null}
				</Grid>
			</Col>
		</Grid>
	)
}

function GuestDetails({ initialValues }: { initialValues?: NewItemSchema }) {
	const { value: isAgent } = useFieldValue("is_agent")
	const { value: tourist } = useFieldValue<NewItemSchema["tourist"] | null>(
		"tourist"
	)
	const shouldExpand =
		tourist?.email ||
		tourist?.address?.location ||
		tourist?.nationality ||
		tourist?.name ||
		tourist?.phone_numbers.number
	return (
		<Component initialState={shouldExpand ? true : false}>
			{({ state, setState }) =>
				state || !isTruthy(isAgent) ? (
					<Grid gap="4">
						<Col sm={12}>
							<Stack gap="1">
								<Text fontSize="md" fontWeight="semibold">
									Guest Details
								</Text>
								<Text color="muted" fontSize="sm">
									Add the Guest's Contact Details
								</Text>
							</Stack>
						</Col>
						<Col xs={12} sm={3}>
							<SelectField
								select={Select}
								placeholder={"e.g. Mr."}
								name={`tourist.salutation`}
								label="Salutation"
								options={["Mr.", "Mrs.", "Ms."]}
							/>
						</Col>
						<Col xs={12} sm="auto">
							<TextInputField
								name={`tourist.name`}
								label="Name"
								required
								placeholder="Anoop Rai"
								type="text"
								maxWidth="xs"
							/>
						</Col>
						<Col sm="auto">
							<PhoneInputField
								name={`tourist.phone_numbers`}
								label="Phone Number(s)"
								required
							/>
						</Col>
						<Col sm="auto">
							<Component
								initialState={
									initialValues?.tourist?.email || tourist?.email ? true : false
								}
							>
								{({ state, setState }) =>
									state ? (
										<TextInputField
											name={`tourist.email`}
											label="Email"
											secondaryLabel="optional"
											type="email"
											placeholder="user@domain.com"
											maxWidth="xs"
										/>
									) : (
										<Stack gap="1">
											<Box display={{ xs: "none", sm: "block" }}>&nbsp;</Box>
											<Box>
												<Button
													onClick={() => setState(true)}
													title="Click to add Email Address"
												>
													<Icons.Mail />
												</Button>
											</Box>
										</Stack>
									)
								}
							</Component>
						</Col>
						<Col sm="auto">
							<Component
								initialState={
									tourist?.address?.location || tourist?.nationality
										? true
										: false
								}
							>
								{({ state, setState }) =>
									state ? (
										<Inline flexWrap="wrap" gap="4">
											<AddressInputField
												name={`tourist.address`}
												label="Origin City/State"
												onlyLocation
												hidePreviewForLocation
											/>
											<SelectField
												select={SelectWorldCountries}
												name={`tourist.nationality`}
												label="Nationality"
												required
												placeholder="e.g. India"
												fetchOnMount
												labelKey="nationality"
												optionRenderer={({ option }) => (
													<Text>{option.name}</Text>
												)}
												cacheKey={`trip-detail-tourist-nationality`}
											/>
										</Inline>
									) : (
										<Stack gap="1">
											<Box display={{ xs: "none", sm: "block" }}>&nbsp;</Box>
											<Box>
												<Button
													onClick={() => setState(true)}
													title="Click to add Origin City/State"
												>
													<Icons.LocationMarker />
												</Button>
											</Box>
										</Stack>
									)
								}
							</Component>
						</Col>
					</Grid>
				) : (
					<Box>
						<Button
							onClick={() => setState(true)}
							title="Click to add guest details"
							level="secondary"
						>
							<Icons.Plus /> Add Guest Details {isAgent ? "(Optional)" : ""}
						</Button>
					</Box>
				)
			}
		</Component>
	)
}

function NewLead({
	initialValues,
	readOnlySource,
	showTeam,
	readOnlyDates,
	readOnlyPax,
	hideComments,
	hideTags,
	showDuplicateExceptId,
}: {
	initialValues: NewItemSchema
	readOnlySource?: boolean
	showTeam?: boolean
	readOnlyDates?: boolean
	readOnlyPax?: boolean
	hideComments?: boolean
	hideTags?: boolean
	showDuplicateExceptId?: number
}) {
	const form = useForm()
	const { value: leadPurpose } = useFieldValue<ILeadPurpose>("lead_purpose")
	const config = leadPurpose?.config
	return (
		<>
			<QuerySourceEditor showTeam={showTeam} />
			<Divider />

			{/* Lead Source */}
			<LeadSource
				initialValues={initialValues}
				readOnlySource={readOnlySource}
			/>

			{/* Travel Dates and Guests */}
			{leadPurpose && config.travel_dates ? (
				<Fragment>
					<Divider />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Inline gap="2" alignItems="center">
									<Icons.LocationMarker size="6" />{" "}
									<Heading fontSize="lg">Travel Dates and Guests</Heading>
								</Inline>
								<Text color="muted">
									Please provide basic details such as Dates along with number
									of adults and children with ages
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<Grid gap="4">
									<Col xs="auto">
										<DatePickerField
											name="start_date"
											label="Start Date"
											required
											disabled={readOnlyDates}
											help={
												<Field<Date | undefined>
													name="start_date"
													subscription={{ value: true }}
												>
													{({ input: { value: start_date } }) =>
														start_date ? (
															<>
																{isBefore(
																	start_date,
																	startOf(new Date(), "day")
																) ? (
																	<Box color="warning" as="span">
																		<Icons.Attention /> You have selected a past
																		date.
																	</Box>
																) : null}
															</>
														) : null
													}
												</Field>
											}
										/>
									</Col>
									<Col xs="auto">
										<TextInputField
											name="no_of_nights"
											label="No. of Nights"
											type="number"
											min={0}
											required
											disabled={readOnlyDates}
											style={{ maxWidth: "140px" }}
											onChange={(e) => {
												form.batch(() => {
													form.change(
														"no_of_nights",
														e.currentTarget.value as never as number
													)
													form.change(
														"no_of_days",
														isNaN(parseInt(e.currentTarget.value))
															? 0
															: parseInt(e.currentTarget.value) + 1
													)
												})
											}}
											help={
												<Field<Date | undefined>
													name="start_date"
													subscription={{ value: true }}
												>
													{({ input: { value: start_date } }) => (
														<Field<number | undefined>
															name="no_of_days"
															subscription={{ value: true }}
														>
															{({ input: { value: no_of_days } }) =>
																no_of_days ? (
																	<Stack gap="1" flexWrap="wrap">
																		<Text fontWeight="semibold" fontSize="base">
																			{start_date
																				? `End Date : ${formatDate(
																						endOf(
																							addUnit(
																								start_date,
																								no_of_days - 1,
																								"days"
																							),
																							"day"
																						),
																						"DD MMM YYYY"
																					)}`
																				: null}
																		</Text>
																		{start_date &&
																		isBefore(
																			addUnit(start_date, no_of_days, "days"),
																			startOf(new Date(), "day")
																		) ? (
																			<Box color="warning" as="span">
																				<Icons.Attention /> You have selected a
																				past date.
																			</Box>
																		) : null}
																	</Stack>
																) : null
															}
														</Field>
													)}
												</Field>
											}
										/>
									</Col>
								</Grid>

								<Grid gap="4">
									<Col xs="auto">
										<TextInputField
											name="no_of_adults"
											label="No. of Adults"
											type="number"
											min={1}
											max={1000}
											required
											disabled={readOnlyPax}
											style={{ maxWidth: "100px" }}
										/>
									</Col>
									<Col xs="auto">
										<ChildrenInputField
											name="children"
											disabled={readOnlyPax}
											maxAge={12}
										/>
									</Col>
								</Grid>
							</Stack>
						</Col>
					</Grid>
				</Fragment>
			) : null}

			<ExistingTrips exceptId={showDuplicateExceptId}>
				{({ trips }) =>
					trips ? (
						<>
							<Divider />
							<Box paddingTop="2">
								<Alert status="warning" title="Possible Duplicate Query">
									<Stack gap="4">
										<Text>
											There are some ({trips.meta.total}) existing queries with
											above phone numbers. Please review the details before
											proceeding further.
										</Text>
										<Box color="default">
											<QueriesTableView items={trips.data} />
										</Box>
									</Stack>
								</Alert>
							</Box>
						</>
					) : null
				}
			</ExistingTrips>

			{/* Comments and Notes */}
			{!hideComments ? (
				<>
					<Divider />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Inline gap="2" alignItems="center">
									<Icons.Annotation size="6" />{" "}
									<Heading fontSize="lg">Comments or Notes</Heading>
								</Inline>
								<Box color="muted">Add useful context for the sales team.</Box>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextAreaInputField
									name="comments"
									label="Comments"
									secondaryLabel="optional"
									placeholder="Only 5 star hotels"
								/>
								{!hideTags ? (
									<Box>
										<SelectField
											name="tags"
											select={SelectTags}
											creatable={false}
											label="Tags"
											secondaryLabel="optional"
											type="trips"
											multiple
											placeholder="Search & select tags(s)..."
											fetchOnMount
										/>
									</Box>
								) : null}
							</Stack>
						</Col>
					</Grid>
				</>
			) : null}
		</>
	)
}

function LeadSource({
	initialValues,
	readOnlySource,
}: {
	initialValues: NewItemSchema
	readOnlySource?: boolean
}) {
	const form = useForm()
	const { value: isLinked } = useFieldValue("link_to_existing_trip")
	const { value: tripSource } = useFieldValue<ITripSource | null>("trip_source")
	return (
		<Grid gap="4">
			<Col sm={12} md={4}>
				<Stack gap="1">
					<Inline gap="2" alignItems="center">
						<Icons.Share size="6" />{" "}
						<Heading fontSize="lg">Lead Source</Heading>
					</Inline>
					<Text color="muted">Choose who this lead came from</Text>
				</Stack>
			</Col>
			<Col>
				<GetFieldValue<"0" | "1" | undefined> name="is_agent">
					{({ value: isAgent }) => (
						<Stack>
							<Stack gap="4">
								<Stack gap="2">
									<Inline gap="2">
										<Stack
											padding="4"
											bgColor={isAgent === "1" ? "primary" : "default"}
											as="label"
											cursor="pointer"
											borderWidth="1"
											borderRadius="md"
											borderColor={isAgent === "1" ? "primary" : "emphasis"}
										>
											<RadioInputField
												name="is_agent"
												label="B2B"
												value="1"
												hideErrors
												onChange={() => {
													form.change("is_agent", "1")
													if (!isTruthy(tripSource?.is_agent)) {
														form.change("trip_source", undefined)
														form.change("trip_source_contact", undefined)
													}
												}}
												disabled={isTruthy(isLinked)}
											/>
										</Stack>

										<Stack
											padding="4"
											bgColor={isAgent === "0" ? "primary" : "default"}
											as="label"
											cursor="pointer"
											borderWidth="1"
											borderRadius="md"
											borderColor={isAgent === "0" ? "primary" : "emphasis"}
										>
											<RadioInputField
												name="is_agent"
												label="B2C"
												value="0"
												hideErrors
												disabled={isTruthy(isLinked)}
											/>
										</Stack>
									</Inline>
									<ErrorMessage name="is_agent" />
								</Stack>
								{isAgent ? (
									<>
										{isTruthy(isAgent) ? (
											<Grid gap="4">
												<Col xs={12} sm>
													<SelectField
														select={SelectTripSources}
														name="trip_source"
														label="Lead Source"
														help={
															<GetFieldValue<
																ITripSource | undefined
															> name="trip_source">
																{({ value }) => (
																	<>
																		{value && (
																			<Inline
																				gap="1"
																				alignItems="center"
																				flexWrap="wrap"
																			>
																				<Text fontWeight="semibold">
																					{value.address?.location.name}
																				</Text>
																				{value.tags?.map(
																					(tag: ITripSourceTag) => {
																						return (
																							<Badge key={tag.id}>
																								{tag.name}
																							</Badge>
																						)
																					}
																				)}
																				{value.teams && value.teams.length
																					? value.teams.map((team) => {
																							return (
																								<Badge warning key={team.id}>
																									{team.name}
																								</Badge>
																							)
																						})
																					: null}
																			</Inline>
																		)}
																	</>
																)}
															</GetFieldValue>
														}
														isAgent={1}
														includeTeams
														includeContacts
														required
														multiple={false}
														fetchOnMount={!initialValues.trip_source}
														creatable
														disabled={readOnlySource || isTruthy(isLinked)}
														onChange={(value: ITripSource | undefined) => {
															form.change("trip_source", value)
															if (
																value?.contacts?.length === 1 &&
																value.is_agent
															) {
																form.change(
																	"trip_source_contact",
																	value?.contacts?.at(0)
																)
															} else {
																form.change("trip_source_contact", undefined)
															}
														}}
														onCreateSuccess={(tripSource: ITripSource) => {
															form.change("trip_source", tripSource)
															if (
																tripSource?.contacts?.length === 1 &&
																tripSource.is_agent
															) {
																form.change(
																	"trip_source_contact",
																	tripSource?.contacts?.at(0)
																)
															} else {
																form.change("trip_source_contact", undefined)
															}
														}}
													/>
												</Col>

												<GetFieldValue<ITripSource | null> name="trip_source">
													{({ value }) =>
														value &&
														isTruthy(isAgent) &&
														isTruthy(value.is_agent) ? (
															<Col>
																<SelectField
																	tripSourceId={value.id}
																	select={SelectTripSourceContact}
																	name="trip_source_contact"
																	label="Contact / Enquiry Person"
																	required
																	creatable
																	disabled={readOnlySource}
																	onCreateSuccess={(contact) => {
																		form.change("trip_source_contact", contact)
																	}}
																/>
															</Col>
														) : null
													}
												</GetFieldValue>
											</Grid>
										) : null}
										<Grid gap="4">
											<Col>
												<Box>
													<TextInputField
														name="reference_id"
														type="text"
														label="Reference ID"
														secondaryLabel="optional"
														placeholder="1231231"
														help="A custom id for your reference regarding the query"
														maxWidth="xs"
													/>
												</Box>
											</Col>
										</Grid>
									</>
								) : null}
							</Stack>
							{isAgent ? (
								<>
									<Divider sm />
									<GuestDetails initialValues={initialValues} />
								</>
							) : null}
						</Stack>
					)}
				</GetFieldValue>
			</Col>
		</Grid>
	)
}

function LinkToExisting({
	setHideFooter,
	initialValues,
	readOnlySource,
	showTeam,
	disableCreateNew,
}: {
	setHideFooter: React.Dispatch<React.SetStateAction<boolean>>
	initialValues: NewItemSchema
	readOnlySource?: boolean
	showTeam?: boolean
	disableCreateNew?: boolean
}) {
	const [selectedTrip, setSelectedTrip] = useState<ITrip | null>(
		initialValues?.trip || null
	)
	const [step, setStep] = useState(initialValues.trip ? 2 : 1)
	const [search, setSearch] = useState("")

	useEffect(() => {
		setHideFooter(step === 1)
	}, [step, setHideFooter])

	return step === 1 ? (
		<SelectTrip
			selectedTrip={selectedTrip}
			setSelectedTrip={setSelectedTrip}
			setStep={setStep}
			setSearch={setSearch}
			search={search}
		/>
	) : step === 2 ? (
		<ExistingLinkedTrip
			trip={selectedTrip}
			setStep={setStep}
			readOnlySource={readOnlySource}
			showTeam={showTeam}
			initialValues={initialValues}
			disableCreateNew={disableCreateNew}
		/>
	) : null
}

function SelectTrip({
	selectedTrip,
	search,
	setSelectedTrip,
	setStep,
	setSearch,
}: {
	selectedTrip: ITrip | null
	search: string
	setSelectedTrip: React.Dispatch<React.SetStateAction<ITrip | null>>
	setStep: React.Dispatch<React.SetStateAction<number>>
	setSearch: React.Dispatch<React.SetStateAction<string>>
}) {
	const { value: suggestedTripsFromApi } = useFieldValue<ITrip[] | null>(
		"suggested_trips"
	)
	const xhr = useXHR()
	const form = useForm<NewItemSchema>()
	const debouncedSearch = useDebounce(search, 500)
	const { data, isLoading } = useSWR(
		debouncedSearch ? ["/existing-trips-linkage", debouncedSearch] : null,
		([url, q]) =>
			xhr.get(url, {
				params: {
					q,
				},
			})
	)

	const handleOnChange = useCallback(
		(trip: ITrip) => {
			setSelectedTrip(trip)
			const values = deriveFormValuesFromTrip(trip)
			form.batch(() => {
				Object.entries(values).forEach(([key, value]) => {
					form.change(key as keyof NewItemSchema, value as never)
				})
				form.change("lead_purpose", null)
			})
		},
		[form, setSelectedTrip]
	)

	const suggestedTrips: ITrip[] =
		data?.data?.data ?? suggestedTripsFromApi ?? []

	return (
		<Stack
			borderWidth="1"
			borderColor="primary_emphasis"
			bgColor="primary"
			rounded="md"
			padding="4"
		>
			<Inline alignItems="start" gap="2">
				<Icons.Info size="5" color="primary" />

				<Stack gap="2" color="primary">
					<Text fontWeight="semibold">Existing Trips Found</Text>
					<Text fontSize="base">
						Search by Trip ID, guest or phone. Details below will appear only
						after you select and confirm the correct Trip.
					</Text>
				</Stack>
			</Inline>
			<Divider sm />
			<Stack gap="4">
				<Input
					value={search}
					name="search"
					style={{ width: "300px" }}
					placeholder="Search by guest name, phone number..."
					onChange={(e) => {
						setSearch(e.currentTarget.value)
					}}
				/>
				{isLoading ? (
					<div>Loading...</div>
				) : suggestedTrips.length > 0 ? (
					<Table
						bordered
						responsive
						headers={[
							"",
							"Trip ID",
							"Guest / B2B",
							"B2B Source",
							"Details",
							"Team Members",
						]}
						rows={suggestedTrips.map((trip) => {
							const {
								id,
								tourist,
								no_of_adults,
								no_of_children,
								current_status,
								trip_source,
								trip_source_contact,
								reference_id,
								start_date,
								destinations,
								days,
								nights,
								team,
								created_at,
							} = trip
							return [
								<RadioInput
									checked={selectedTrip?.id === id}
									onChange={() => {
										handleOnChange(trip)
									}}
									cursor="pointer"
								/>,
								<Stack gap="1">
									<Link
										anchored
										target="_blank"
										to={`/trips/${id}`}
										color={"accent"}
										fontWeight="semibold"
									>
										{id}
									</Link>
									<Box>
										<Badge
											status={
												current_status === "Converted" ? "success" : undefined
											}
										>
											{current_status}
										</Badge>
									</Box>
								</Stack>,
								tourist ? (
									<Stack gap="px">
										<Text fontWeight="semibold">{tourist.name}</Text>
										<Box fontSize="sm" color="muted">
											{joinAttributes(
												<Text as="span">
													{no_of_adults}A
													{no_of_children ? `, ${no_of_children}C` : null}
												</Text>,
												[
													tourist.phone_numbers?.length,
													<PhoneNumber
														value={tourist.phone_numbers}
														iconOnly
													/>,
												],
												[
													tourist.email,
													<Email value={tourist.email} iconOnly />,
												]
											)}
										</Box>
									</Stack>
								) : (
									"-"
								),
								<Stack gap="px">
									<Text fontWeight="semibold">{trip_source.short_name}</Text>
									{reference_id ? (
										<Text fontSize="sm">Ref# {reference_id}</Text>
									) : null}
									{trip_source_contact ? (
										<Inline
											alignItems="center"
											flexWrap="wrap"
											gap="2"
											fontSize="sm"
										>
											<Box color="muted">{trip_source_contact.name}</Box>
											<Box display="inlineBlock">
												{joinAttributes([
													trip_source_contact.phone_numbers?.length,
													<PhoneNumber
														value={trip_source_contact.phone_numbers}
														iconOnly
													/>,
												])}
											</Box>
										</Inline>
									) : null}
								</Stack>,
								<Stack gap="px">
									<Text style={{ maxWidth: "140px" }} textOverflow="truncate">
										{destinations.map((d) => d.name).join(", ")}
									</Text>
									<Box fontSize="sm" color="muted">
										{joinAttributes(
											<Time timestamp={start_date} />,
											<TimeDuration value={duration(days, "days")}>
												{nights}N
											</TimeDuration>
										)}
									</Box>
								</Stack>,
								<Stack gap="px">
									{team?.length ? (
										<Stack style={{ width: "80px" }} gap="px">
											{team.map((t) => (
												<Text key={t.id} fontSize="sm" color="muted">
													{t.name}
												</Text>
											))}
										</Stack>
									) : null}
									<RelativeTime fontSize="sm" timestamp={created_at} />
								</Stack>,
							]
						})}
					/>
				) : (
					<Stack
						gap="2"
						padding="4"
						bgColor="white"
						borderWidth="1"
						rounded="md"
					>
						<Text fontWeight="semibold" color="primary">
							No Trips Found to Select
						</Text>
						<Text fontSize="base" color="muted">
							Search above by Trip ID, guest or phone to look across all trips
							or create a new lead.
						</Text>
					</Stack>
				)}
				<Box>
					<Button
						style={{ backgroundColor: "white" }}
						color="primary"
						status="primary"
						disabled={!selectedTrip}
						onClick={() => setStep(2)}
					>
						Select Trip {selectedTrip ? ` #${selectedTrip.id}` : ``}
					</Button>
				</Box>
			</Stack>
		</Stack>
	)
}

function ExistingLinkedTrip({
	trip,
	setStep,
	initialValues,
	readOnlySource,
	showTeam,
	disableCreateNew,
}: {
	trip: ITrip | null
	setStep: React.Dispatch<React.SetStateAction<number>>
	initialValues: NewItemSchema
	readOnlySource?: boolean
	showTeam?: boolean
	disableCreateNew?: boolean
}) {
	return (
		<Stack gap="4">
			<SelectedTripDetails
				trip={trip}
				setStep={setStep}
				disableCreateNew={disableCreateNew}
			/>
			<Divider sm />
			<QuerySourceEditor showTeam={showTeam} />
			<Divider sm />
			<LeadSource
				initialValues={initialValues}
				readOnlySource={readOnlySource}
			/>
		</Stack>
	)
}

function SelectedTripDetails({
	trip,
	setStep,
	disableCreateNew,
}: {
	trip: ITrip | null
	setStep: React.Dispatch<React.SetStateAction<number>>
	disableCreateNew?: boolean
}) {
	if (!trip) {
		return null
	}
	const {
		id,
		current_status,
		tourist,
		no_of_adults,
		no_of_children,
		trip_source,
		destinations,
		start_date,
		days,
		nights,
		team,
	} = trip
	return (
		<Inline
			justifyContent="between"
			borderWidth="1"
			borderColor="muted"
			padding="4"
			rounded="md"
			flexWrap="wrap"
			gap="6"
		>
			<Stack gap="2">
				<Text fontWeight="semibold" color="muted" fontSize="sm">
					Trip ID
				</Text>
				<Stack gap="1">
					<Link
						anchored
						to={`/trips/${id}`}
						color={"accent"}
						fontWeight="semibold"
						target="_blank"
					>
						Trip #{id}
					</Link>
					<Badge
						status={current_status === "Converted" ? "success" : undefined}
					>
						{current_status}
					</Badge>
				</Stack>
			</Stack>

			<Stack gap="2">
				<Text fontWeight="semibold" color="muted" fontSize="sm">
					GUEST / B2B
				</Text>

				{tourist ? (
					<Stack gap="px">
						<Text fontWeight="semibold">{tourist.name}</Text>
						<Box fontSize="sm" color="muted">
							{joinAttributes(
								<Text as="span">
									{no_of_adults}A
									{no_of_children ? `, ${no_of_children}C` : null}
								</Text>,
								[
									tourist.phone_numbers?.length,
									<PhoneNumber value={tourist.phone_numbers} iconOnly />,
								],
								[tourist.email, <Email value={tourist.email} iconOnly />]
							)}
						</Box>
					</Stack>
				) : (
					"-"
				)}
			</Stack>

			<Stack gap="2">
				<Text fontWeight="semibold" color="muted" fontSize="sm">
					SOURCE
				</Text>
				<Stack gap="px">
					<Text fontWeight="semibold">{trip_source.short_name}</Text>
				</Stack>
			</Stack>

			<Stack gap="2">
				<Text fontWeight="semibold" color="muted" fontSize="sm">
					TRIP DETAILS
				</Text>

				<Stack gap="px">
					<Text style={{ maxWidth: "140px" }} textOverflow="truncate">
						{destinations?.map((d) => d.name).join(", ")}
					</Text>
					<Box fontSize="sm" color="muted">
						{joinAttributes(
							<Time timestamp={start_date} />,
							<TimeDuration value={duration(days, "days")}>
								{nights}N
							</TimeDuration>
						)}
					</Box>
				</Stack>
			</Stack>

			<Stack gap="2">
				<Text fontWeight="semibold" color="muted" fontSize="sm">
					TEAM MEMBER
				</Text>

				<Stack gap="px">
					{team?.length ? (
						<Stack style={{ width: "80px" }} gap="px">
							{team.map((t) => (
								<Text key={t.id} fontSize="sm" color="muted">
									{t.name}
								</Text>
							))}
						</Stack>
					) : (
						"-"
					)}
				</Stack>
			</Stack>

			{!disableCreateNew ? (
				<Button status="primary" onClick={() => setStep(1)}>
					Change
				</Button>
			) : null}
		</Inline>
	)
}

function deriveFormValuesFromTrip(trip: ITrip) {
	const is_agent = trip.trip_source.is_agent ? "1" : "0"
	const tourist = {
		name: trip.tourist?.name_without_salutation,
		email: trip.tourist?.email || "",
		phone_numbers: trip.tourist?.phone_numbers?.[0]
			? getEditablePhoneNumber(
					trip.tourist.phone_numbers[0].phone_number,
					trip.tourist.phone_numbers[0].country_code
				)
			: { number: "", country_code: "IN", is_primary: true },
		address: transformAddressToEditableValue(trip.tourist?.address),
		salutation: trip.tourist?.salutation || "Mr.",
		nationality: trip.tourist?.nationality,
	}
	const hotelsList = trip.latest_given_quote?.quote?.hotels || []
	const uniqueHotels = [
		...new Map(hotelsList.map((h) => [h.hotel_id, h.hotel])).values(),
	]
	return {
		trip_id: String(trip.id),
		is_agent,
		trip_source: trip.trip_source,
		trip_source_contact: trip.trip_source_contact,
		tourist,
		hotels_of_interest: uniqueHotels,
	}
}

export function AddNewLeadInDialog({
	children,
	onSuccess,
}: {
	children: (props: { create: () => void }) => React.ReactNode
	onSuccess?: () => void
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()
	const navigate = useNavigate()
	return (
		<>
			{children({ create: openDialog })}
			<Dialog open={isOpenDialog} onClose={closeDialog} title="Create Lead" lg>
				<Dialog.Body>
					<LeadDetailsForm
						mode="create"
						onCancel={closeDialog}
						onSubmit={async (data, intent) => {
							const move_to_trips = intent === "MOVE_TO_TRIP"
							const res = await LeadXHR(xhr).createLead({
								...data,
								move_to_trips: move_to_trips ? 1 : undefined,
							})
							if (move_to_trips && res.trip_id) {
								showSnackbar(
									"Lead created and attached to the trip successfully."
								)
								navigate("/trips/:tripId/new-quote", {
									params: { tripId: String(res.trip_id) },
								})
								return
							}
							closeDialog()
							onSuccess?.()
							if (data.link_to_existing_trip === 1) {
								showSnackbar("Lead Linked successfully.")
							} else {
								showSnackbar("Lead created successfully.")
							}
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function isRequired(value?: boolean | "conditional") {
	return value === true
}
